import React, { useEffect, useState } from 'react';
import { BundleCreationProvider } from '../contexts/BundleCreationContext';
import BundleCreationWorkflow from '../components/bundle/BundleCreationWorkflow';
import { WarehouseAPI } from '../services/api';
import { usePermission } from '../hooks/usePermission';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';

const CreateBundle: React.FC = () => {
  // RBAC: the entire page is a bundle-creation (add-bundle) mutation flow. A view-only
  // caller gets a read-only notice instead of the workflow; the backend independently
  // rejects any write with 403 (defense in depth, SI-5).
  const canEdit = usePermission('add-bundle').can('edit');
  const [loading, setLoading] = useState(true);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    WarehouseAPI.getUnicommerceStatus()
      .then((data) => {
        if (!mounted) return;
        if (data?.isRunning) {
          const msg = data.serverMessage || 'Inventory Syncing in progress';
          setBlockedMessage(msg);
        } else {
          setBlockedMessage(null);
        }
      })
      .catch(() => {
        // If status check fails, be permissive (allow access) but do not block creation.
        setBlockedMessage(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canEdit) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 6 }}>
        <Alert severity="info">
          <Typography variant="h6">View-only access</Typography>
          <Typography variant="body2">You don&apos;t have edit access to create bundles. Contact an admin if you need it.</Typography>
        </Alert>
      </Box>
    );
  }

  if (blockedMessage) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 6 }}>
        <Alert severity="info">
          <Typography variant="h6">{blockedMessage}</Typography>
          <Typography variant="body2">Please try again after the inventory sync completes.</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <BundleCreationProvider>
      <BundleCreationWorkflow />
    </BundleCreationProvider>
  );
};

export default CreateBundle;